import {match} from 'ts-pattern';
import type {StoreApi} from 'zustand/vanilla';

import {move} from 'ramda';

import type {ColumnsSetting} from '../../types/Api';
import type {EagColumnState} from '../../types/EagColumnState';
import {getSortModel} from '../../utils/getSortModel';
import type {EagGridState, EagGridStore} from '../createEagGridStore';

type ColumnActions = Pick<
  EagGridStore,
  | 'setColumnStates'
  | 'setColumnWidth'
  | 'setColumnHidden'
  | 'setColumnPinned'
  | 'reorderColumnsByKey'
  | 'reorderColumnBlock'
  | 'setColumnSort'
  | 'applyPresetColumns'
  | 'applyPresetColumnsAndSort'
  | 'applyMultiSort'
  | 'removeSortColumn'
  | 'clearSortModel'
>;

/** Column actions. Bodies moved verbatim from createEagGridStore.ts. */
export function createColumnActions(store: StoreApi<EagGridState>): ColumnActions {
  /** Re-numbers sortIndex values to be contiguous (0, 1, 2...) after a removal. */
  function recompactSortIndex(columnStates: EagColumnState[]): EagColumnState[] {
    const sorted = columnStates
      .filter((col) => col.sort != null && col.sortIndex != null)
      .toSorted((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0));
    const indexMap = new Map(sorted.map((col, i) => [col.key, i]));
    return columnStates.map((col) => {
      const newIndex = indexMap.get(col.key);
      return newIndex != null ? {...col, sortIndex: newIndex} : col;
    });
  }

  /**
   * Shared setter for single-field column patches (width/hidden/pinned). Finds the
   * target column, no-ops if the field already equals the incoming value (applying
   * `currentDefault` to the stored field for the comparison, matching each setter's
   * per-field default), otherwise rebuilds columnStates with the field replaced.
   * Mirrors the find-miss fall-through: a missing key still rebuilds the array.
   */
  function patchColumn<K extends keyof EagColumnState>(
    key: string,
    field: K,
    value: EagColumnState[K],
    currentDefault: (current: EagColumnState[K]) => EagColumnState[K]
  ): void {
    const state = store.getState();
    const target = state.columnStates.find((col) => col.key === key);
    if (target && currentDefault(target[field]) === value) {
      return;
    }
    store.setState({
      columnStates: state.columnStates.map((col) =>
        col.key === key ? {...col, [field]: value} : col
      ),
    });
  }

  return {
    setColumnStates: (columnStates: EagColumnState[]) => {
      store.setState({columnStates});
    },

    setColumnWidth: (key: string, width: number) => {
      patchColumn(key, 'width', width, (current) => current);
    },

    setColumnHidden: (key: string, hidden: boolean) => {
      // Definition-hidden columns can never be unhidden.
      const target = store.getState().columnStates.find((col) => col.key === key);
      if (!hidden && target?.lockedHidden === true) {
        return;
      }
      patchColumn(key, 'hidden', hidden, (current) => current ?? false);
    },

    setColumnPinned: (key: string, pinned: 'LEFT' | 'RIGHT' | null) => {
      patchColumn(key, 'pinned', pinned, (current) => current ?? null);
    },

    reorderColumnsByKey: (fromKey: string, toKey: string) => {
      const state = store.getState();
      const fromIndex = state.columnStates.findIndex((cs) => cs.key === fromKey);
      const toIndex = state.columnStates.findIndex((cs) => cs.key === toKey);
      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
        return;
      }
      const reordered = move(fromIndex, toIndex, state.columnStates);
      store.setState({
        columnStates: reordered.map((col, index) => ({...col, order: index})),
      });
    },

    reorderColumnBlock: (blockKeys: string[], targetKey: string, side: 'left' | 'right') => {
      const state = store.getState();
      const stateByKey = new Map(state.columnStates.map((col) => [col.key, col]));
      // Block order follows blockKeys (the group cell's render order), not store order.
      const block = blockKeys
        .map((key) => stateByKey.get(key))
        .filter((col): col is EagColumnState => col !== undefined);
      const blockSet = new Set(block.map((col) => col.key));
      if (block.length === 0 || blockSet.has(targetKey)) {
        return;
      }
      const remaining = state.columnStates.filter((col) => !blockSet.has(col.key));
      const targetIndex = remaining.findIndex((col) => col.key === targetKey);
      if (targetIndex === -1) {
        return;
      }
      const insertIndex = side === 'left' ? targetIndex : targetIndex + 1;
      const reordered = [
        ...remaining.slice(0, insertIndex),
        ...block,
        ...remaining.slice(insertIndex),
      ];
      store.setState({
        columnStates: reordered.map((col, index) => ({...col, order: index})),
      });
    },

    setColumnSort: (key: string, sort: 'ASC' | 'DESC' | null) => {
      // Single-column sort: only update columnStates. Do NOT touch dataQuery — a new
      // dataQuery reference would invalidate columnConfig in EagGridInner (transformDefinition
      // consumes dataQuery), causing EagGridTable's useLayoutEffect to reset every column
      // back to the preset's initial state (losing user pinning/hiding/width).
      const state = store.getState();
      const sortMode = state.definition?.behavior?.columnSortMode;

      if (sortMode === 'MULTI_SORT') {
        const target = state.columnStates.find((col) => col.key === key);
        if (!target) {
          return;
        }

        if (sort === null) {
          // Remove sort and re-compact
          const without = state.columnStates.map((col) =>
            col.key === key ? {...col, sort: null, sortIndex: undefined} : col
          );
          store.setState({columnStates: recompactSortIndex(without)});
        } else if (target.sort != null) {
          // Already sorted — just update direction, keep sortIndex
          store.setState({
            columnStates: state.columnStates.map((col) => (col.key === key ? {...col, sort} : col)),
          });
        } else {
          // New sort — append with next sortIndex
          const maxIndex = state.columnStates.reduce(
            (max, col) => (col.sortIndex != null ? Math.max(max, col.sortIndex) : max),
            -1
          );
          store.setState({
            columnStates: state.columnStates.map((col) =>
              col.key === key ? {...col, sort, sortIndex: maxIndex + 1} : col
            ),
          });
        }
        return;
      }

      // SINGLE_SORT / NONE — original behavior: clear others
      const alreadyApplied = state.columnStates.every((col) =>
        col.key === key ? (col.sort ?? null) === sort : (col.sort ?? null) === null
      );
      if (alreadyApplied) {
        return;
      }
      store.setState({
        columnStates: state.columnStates.map((col) =>
          col.key === key
            ? {...col, sort, sortIndex: sort ? 0 : undefined}
            : {...col, sort: null, sortIndex: undefined}
        ),
      });
    },

    applyMultiSort: (entries) => {
      const state = store.getState();
      const entryMap = new Map(entries.map((e, i) => [e.columnKey, {sort: e.order, sortIndex: i}]));
      store.setState({
        columnStates: state.columnStates.map((col) => {
          const entry = entryMap.get(col.key);
          return entry
            ? {...col, sort: entry.sort, sortIndex: entry.sortIndex}
            : {...col, sort: null, sortIndex: undefined};
        }),
      });
    },

    removeSortColumn: (key: string) => {
      const state = store.getState();
      const target = state.columnStates.find((col) => col.key === key);
      if (!target || target.sort == null) {
        return;
      }
      const without = state.columnStates.map((col) =>
        col.key === key ? {...col, sort: null, sortIndex: undefined} : col
      );
      store.setState({columnStates: recompactSortIndex(without)});
    },

    clearSortModel: () => {
      const state = store.getState();
      if (!state.columnStates.some((col) => col.sort != null)) {
        return;
      }
      store.setState({
        columnStates: state.columnStates.map((col) =>
          col.sort != null || col.sortIndex != null
            ? {...col, sort: null, sortIndex: undefined}
            : col
        ),
      });
    },

    applyPresetColumns: (columns: ColumnsSetting[]) => {
      const state = store.getState();
      const presetMap = new Map(columns.map((column) => [column.columnKey, column]));
      store.setState({
        isColumnsReady: true,
        columnStates: state.columnStates.map((col) =>
          match(presetMap.get(col.key))
            .with(undefined, () => col)
            .otherwise((preset) => ({
              ...col,
              // lockedHidden (definition-hidden) columns stay hidden regardless of the preset.
              hidden: col.lockedHidden === true || !preset.visible,
              width: preset.width,
              pinned: preset.pinned,
            }))
        ),
      });
    },

    applyPresetColumnsAndSort: (columns: ColumnsSetting[]) => {
      const state = store.getState();
      // Read current sort from columnStates rather than dataQuery.order. columnStates is
      // the live source of truth — it reflects the user's most recent sort click, which
      // setColumnSort no longer mirrors into dataQuery (that would trigger a column-reset
      // cascade via columnConfig recomputation).
      const presetMap = new Map(columns.map((column) => [column.columnKey, column]));
      const presetIndexMap = new Map(columns.map((column, i) => [column.columnKey, i]));
      const sortMap = new Map(
        getSortModel(state.columnStates).map((entry, i) => [
          entry.columnKey,
          {sort: entry.order, sortIndex: i},
        ])
      );

      const patched = state.columnStates.map((col) => {
        const preset = presetMap.get(col.key);
        const sortEntry = sortMap.get(col.key);
        const sortFields = sortEntry
          ? {sort: sortEntry.sort, sortIndex: sortEntry.sortIndex}
          : {sort: null as 'ASC' | 'DESC' | null, sortIndex: undefined};

        return preset
          ? {
              ...col,
              // lockedHidden (definition-hidden) columns stay hidden regardless of the preset.
              hidden: col.lockedHidden === true || !preset.visible,
              width: preset.width,
              pinned: preset.pinned,
              ...sortFields,
            }
          : {...col, ...sortFields};
      });

      // Entry order IS preset state (see areColumnsAndSettingsEqual): a reload seeds
      // columnStates sorted by the preset's columnsSettings index (transformDefinition's
      // colIndexMap comparator), so applying a preset must reorder the live states the
      // same way — preset-listed columns in preset index order, the rest (technical /
      // definition-drift columns) after them keeping their relative order. Without this,
      // reset-columns / preset activation patches widths+visibility but leaves the user's
      // dragged order on screen until the next reload (T20-134773). `order` is renumbered
      // to the new index because render order sorts by the field, not the array
      // (useOrderedVisibleColumns), mirroring reorderColumnsByKey.
      store.setState({
        isColumnsReady: true,
        columnStates: patched
          .toSorted((a, b) => {
            const aIndex = presetIndexMap.get(a.key) ?? -1;
            const bIndex = presetIndexMap.get(b.key) ?? -1;
            if (aIndex === -1 && bIndex === -1) {
              return 0;
            }
            if (aIndex === -1) {
              return 1;
            }
            if (bIndex === -1) {
              return -1;
            }
            return aIndex - bIndex;
          })
          .map((col, index) => ({...col, order: index})),
      });
    },
  };
}
